import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import SectionTitle from "@/components/base/SectionTitle";
import NotFoundPage from "@/pages/NotFound";
import FilterBar from "@/pages/category/components/FilterBar";
import {
  getChannelBySlug,
  getByChannel,
  filterNews,
  sortNews,
  collectTags,
  getHottest,
  type SortKey,
} from "@/utils/news";
import { regions, timeRanges, topics } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function ChannelPage() {
  const { channelSlug = "" } = useParams();
  const channel = getChannelBySlug(channelSlug);

  const [region, setRegion] = useState("全部");
  const [timeRange, setTimeRange] = useState("全部");
  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  const base = useMemo(() => getByChannel(channelSlug), [channelSlug]);
  const tagOptions = useMemo(() => collectTags().slice(0, 12), []);

  useEffect(() => {
    setRegion("全部");
    setTimeRange("全部");
    setTag("全部");
    setSort("latest");
    setPage(1);
  }, [channelSlug]);

  const filtered = useMemo(
    () => sortNews(filterNews(base, { region, timeRange, tag }), sort),
    [base, region, timeRange, tag, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const spotlight = useMemo(() => getHottest(3), []);

  useEffect(() => {
    setPage(1);
  }, [region, timeRange, tag, sort]);

  if (!channel) return <NotFoundPage />;

  const reset = () => {
    setRegion("全部");
    setTimeRange("全部");
    setTag("全部");
    setSort("latest");
    setPage(1);
  };

  return (
    <Layout>
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-5 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "频道" },
            { label: channel.name },
          ]}
        />

        <div className="mt-4 rounded-lg bg-secondary-900 p-5 md:p-7 text-background-50">
          <div className="flex items-start gap-4">
            <span className="hidden sm:flex w-14 h-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-3xl text-background-50">
              <i className={channel.icon} />
            </span>
            <div>
              <span className="inline-block px-2 py-0.5 rounded-md bg-accent-500 text-secondary-950 text-xs font-medium mb-2">
                专题频道
              </span>
              <h1 className="text-2xl md:text-3xl font-black">{channel.name}</h1>
              <p className="mt-2 text-sm md:text-base text-background-200 leading-relaxed max-w-3xl">
                {channel.desc}
              </p>
              <p className="mt-3 text-xs text-background-300">
                共收录 <span className="font-bold text-accent-400">{base.length}</span> 条频道内容
              </p>
            </div>
          </div>
        </div>

        {/* Topic collection recommendation */}
        <section className="mt-6">
          <SectionTitle
            title="专题合集推荐"
            subtitle="精选话题合集，聚合相关报道与爆料"
            icon="ri-bookmark-3-line"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {topics.map((topic) => (
              <Link
                key={topic.title}
                to={`/search?q=${encodeURIComponent(topic.tag)}`}
                className="group flex items-center gap-3 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={topic.cover}
                    alt={topic.title}
                    title={`${topic.title} - 吃瓜爆料网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {topic.title}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{topic.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Spotlight */}
        <section className="mt-8">
          <SectionTitle title="频道热点" subtitle="本频道近期最受关注的内容" icon="ri-fire-line" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {spotlight.map((article) => (
              <NewsCard key={article.id} article={article} variant="grid" />
            ))}
          </div>
        </section>

        {/* Filters + list */}
        <section className="mt-8">
          <SectionTitle title="全部内容" subtitle="可按地区、时间与标签筛选" icon="ri-list-check" />
          <FilterBar
            regions={regions}
            timeRanges={timeRanges}
            tagOptions={tagOptions}
            region={region}
            timeRange={timeRange}
            tag={tag}
            sort={sort}
            onRegion={setRegion}
            onTimeRange={setTimeRange}
            onTag={setTag}
            onSort={(v) => setSort(v as SortKey)}
            onReset={reset}
          />

          <div className="mt-4 flex items-center justify-between text-sm text-foreground-500">
            <span>
              共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 条结果
            </span>
            <span className="hidden sm:inline">
              第 {page} / {totalPages} 页
            </span>
          </div>

          {paged.length > 0 ? (
            <div className="mt-4 flex flex-col gap-3 md:gap-4">
              {paged.map((article) => (
                <NewsCard key={article.id} article={article} variant="horizontal" />
              ))}
            </div>
          ) : (
            <div className="mt-4 flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl">
                <i className="ri-search-eye-line" />
              </span>
              <p className="mt-4 text-foreground-700 font-medium">暂无相关内容</p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          )}

          <Pagination current={page} total={totalPages} onChange={setPage} />
        </section>
      </div>
    </Layout>
  );
}